import React, { createContext, useContext, useEffect, useState } from "react";

const LangCtx = createContext({ lang: "en", setLang: () => {}, toggle: () => {} });

const DEFAULT_LANG = "en";
const STORAGE_KEY = "gcs_lang";

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_LANG;
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const toggle = () => setLang((l) => (l === "en" ? "es" : "en"));

  return <LangCtx.Provider value={{ lang, setLang, toggle }}>{children}</LangCtx.Provider>;
}

export const useLang = () => useContext(LangCtx);

/**
 * t(value, lang) — resolve a bilingual value to a display string.
 * Accepts:
 *   - string: returned as-is
 *   - { en, es }: returns value[lang] with fallback
 *   - null/undefined: returns ""
 */
export function t(value, lang) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value[lang] || value.en || value.es || Object.values(value)[0] || "";
  }
  return String(value);
}

/**
 * UI dictionary for static labels not stored in CMS content.
 * Keep flat keys. Access via ui(key, lang) or via useT().
 */
export const UI = {
  // Nav
  "nav.services": { en: "Services", es: "Servicios" },
  "nav.manifesto": { en: "Manifesto", es: "Manifiesto" },
  "nav.pricing": { en: "Pricing", es: "Precios" },
  "nav.gallery": { en: "Gallery", es: "Galería" },
  "nav.contact": { en: "Contact", es: "Contacto" },

  // Common CTAs
  "cta.book": { en: "Book cleaning", es: "Reservar limpieza" },
  "cta.view_pricing": { en: "See pricing", es: "Ver precios" },
  "cta.quote": { en: "Get a quote", es: "Cotizar" },
  "cta.call": { en: "Call", es: "Llamar" },
  "cta.whatsapp": { en: "WhatsApp", es: "WhatsApp" },
  "cta.scroll": { en: "Scroll to explore", es: "Desliza para explorar" },
  "cta.quote_request": { en: "Request quote", es: "Solicitar cotización" },
  "cta.choose_plan": { en: "Choose", es: "Elegir" },
  "cta.reload": { en: "Reload", es: "Recargar" },

  // Loading
  "state.loading": { en: "Loading…", es: "Cargando…" },
  "state.sending": { en: "Sending…", es: "Enviando…" },

  // Hero small
  "hero.badge_area": { en: "MD · DMV · Bilingual", es: "MD · DMV · Bilingüe" },
  "hero.chapter": { en: "CH. 00 — Welcome", es: "CH. 00 — Bienvenida" },
  "hero.owner_by": { en: "est. Maria Solis", es: "est. Maria Solis" },

  // Services section
  "services.chapter": { en: "CH. 01 — Services", es: "CH. 01 — Servicios" },
  "services.h1_a": { en: "Cleaning tailored to ", es: "Limpieza diseñada para " },
  "services.h1_em": { en: "every space", es: "cada espacio" },
  "services.h1_b": { en: ".", es: "." },
  "services.lead": {
    en: "Competitive rates. No surprises, no fine print. Confirmed quote before we start.",
    es: "Tarifas competitivas. Sin sorpresas, sin letra chica. Cotización confirmada antes de comenzar.",
  },

  // Manifesto
  "manifesto.chapter": { en: "CH. 02 — Manifesto", es: "CH. 02 — Manifiesto" },
  "manifesto.h1_a": { en: "The cleaning standard your home ", es: "El estándar de limpieza que su hogar " },
  "manifesto.h1_em": { en: "deserves", es: "merece" },
  "manifesto.h1_b": { en: ".", es: "." },
  "manifesto.subline": { en: "Six principles", es: "Seis principios" },

  // Pricing
  "pricing.chapter": { en: "CH. 03 — Pricing", es: "CH. 03 — Precios" },
  "pricing.h1_a": { en: "Transparent plans for ", es: "Planes " },
  "pricing.h1_em": { en: "family & business", es: "transparentes" },
  "pricing.h1_b": { en: ".", es: " para familia y comercio." },
  "pricing.lead": {
    en: "Automatic discounts on recurring visits. Prices may vary by size and city; we confirm before starting.",
    es: "Descuentos automáticos en visitas recurrentes. Los precios pueden variar según sqft y ciudad; te confirmamos antes de empezar.",
  },
  "pricing.per_visit": { en: "/ visit", es: "/ visita" },
  "pricing.popular": { en: "Most popular", es: "Más popular" },

  // Gallery
  "gallery.chapter": { en: "CH. 04 — Gallery", es: "CH. 04 — Galería" },
  "gallery.h1_a": { en: "Spaces that speak ", es: "Espacios que hablan por " },
  "gallery.h1_em": { en: "for themselves", es: "sí solos" },
  "gallery.h1_b": { en: ".", es: "." },

  // Before/After
  "ba.chapter": { en: "CH. 04.5 — Before / After", es: "CH. 04.5 — Antes / Después" },
  "ba.h1_a": { en: "See the ", es: "Vea la " },
  "ba.h1_em": { en: "transformation", es: "transformación" },
  "ba.h1_b": { en: ". Drag the handle.", es: ". Arrástre el manejador." },
  "ba.lead": {
    en: "Real cases from families and businesses in Maryland & DMV. Every visit is documented with a photo report.",
    es: "Casos reales de familias y comercios en Maryland & DMV. Cada visita se documenta con reporte fotográfico.",
  },
  "ba.before": { en: "Before", es: "Antes" },
  "ba.after": { en: "After", es: "Después" },
  "ba.case": { en: "Case", es: "Caso" },

  // Testimonials
  "testimonials.chapter": { en: "CH. 05 — Trust", es: "CH. 05 — Confianza" },
  "testimonials.rating": { en: "4.9 · verified", es: "4.9 · verificados" },
  "testimonials.h1_a": { en: "Families who trust ", es: "Familias que confían en " },
  "testimonials.h1_em": { en: "Maria & the team", es: "Maria & el equipo" },
  "testimonials.h1_b": { en: ".", es: "." },

  // Coverage
  "coverage.chapter": { en: "Coverage", es: "Cobertura" },
  "coverage.h1_a": { en: "We serve ", es: "Servimos " },
  "coverage.lead": {
    en: "Not sure if we reach your area? Give us a call and we'll confirm availability with no obligation.",
    es: "¿No estás seguro si llegamos a tu zona? Llámanos y confirmamos disponibilidad sin compromiso.",
  },
  "coverage.md.label": { en: "Maryland", es: "Maryland" },
  "coverage.md.body": {
    en: "Bethesda, Rockville, Silver Spring, Gaithersburg, Columbia, Laurel, Bowie, Upper Marlboro, Lanham, Largo and the metropolitan area.",
    es: "Bethesda, Rockville, Silver Spring, Gaithersburg, Columbia, Laurel, Bowie, Upper Marlboro, Lanham, Largo y área metropolitana.",
  },
  "coverage.dmv.label": { en: "DC & Virginia", es: "DC & Virginia" },
  "coverage.dmv.body": {
    en: "Washington DC, Arlington, Alexandria and nearby suburbs.",
    es: "Washington DC, Arlington, Alexandria y suburbios cercanos.",
  },

  // FAQ
  "faq.chapter": { en: "CH. 07 — FAQ", es: "CH. 07 — FAQ" },
  "faq.h1_a": { en: "Answers to your ", es: "Resolvemos tus " },
  "faq.h1_em": { en: "questions", es: "dudas" },
  "faq.h1_b": { en: ".", es: "." },

  // Contact
  "contact.chapter": { en: "CH. 06 — Book", es: "CH. 06 — Reservar" },
  "contact.h1_a": { en: "Tell us about ", es: "Cuéntanos sobre " },
  "contact.h1_em": { en: "your space", es: "su espacio" },
  "contact.h1_b": { en: ".", es: "." },
  "contact.lead": {
    en: "We send a quote within 2 business hours. No commitment, no fine print.",
    es: "Te enviamos una cotización en menos de 2 horas hábiles. Sin compromisos, sin letra chica.",
  },
  "contact.phone": { en: "Phone", es: "Teléfono" },
  "contact.email": { en: "Email", es: "Email" },
  "contact.area": { en: "Area", es: "Área" },
  "form.name": { en: "Full name *", es: "Nombre completo *" },
  "form.phone": { en: "Phone *", es: "Teléfono *" },
  "form.email": { en: "Email (optional)", es: "Email (opcional)" },
  "form.date": { en: "Preferred date", es: "Fecha preferida" },
  "form.address": { en: "Address", es: "Dirección" },
  "form.city": { en: "City", es: "Ciudad" },
  "form.bedrooms": { en: "Bedrooms", es: "Habitaciones" },
  "form.bathrooms": { en: "Bathrooms", es: "Baños" },
  "form.sqft": { en: "Size (sqft, optional)", es: "Tamaño (sqft, opcional)" },
  "form.notes": { en: "Additional notes", es: "Notas adicionales" },
  "form.disclaimer": {
    en: "By submitting you agree to be contacted by phone or email to confirm details. No spam.",
    es: "Al enviar aceptas ser contactado por teléfono o email para confirmar detalles. Sin spam.",
  },
  "form.submit": { en: "Send request", es: "Enviar solicitud" },
  "form.service": { en: "Service type", es: "Tipo de servicio" },
  "form.frequency": { en: "Frequency", es: "Frecuencia" },
  "form.freq_once": { en: "One-time", es: "Una sola vez" },
  "form.freq_weekly": { en: "Weekly (-15%)", es: "Semanal (-15%)" },
  "form.freq_biweekly": { en: "Biweekly (-10%)", es: "Quincenal (-10%)" },
  "form.freq_monthly": { en: "Monthly (-5%)", es: "Mensual (-5%)" },
  "form.toast_success": {
    en: "Request sent! We'll be in touch within 2 hours.",
    es: "¡Solicitud enviada! Te contactaremos en menos de 2 horas.",
  },
  "form.toast_error": {
    en: "We couldn't send it. Please try again or call us.",
    es: "No se pudo enviar. Intenta nuevamente o llámanos.",
  },
  "form.toast_missing": {
    en: "Please enter your name and phone.",
    es: "Por favor, ingresa tu nombre y teléfono.",
  },

  // Footer
  "footer.contact": { en: "Contact", es: "Contacto" },
  "footer.explore": { en: "Explore", es: "Explorar" },
  "footer.admin": { en: "Admin panel", es: "Panel admin" },
  "footer.rights": { en: "All rights reserved.", es: "Todos los derechos reservados." },
  "footer.tagline_line": { en: "Bilingual · Insured · Eco-friendly", es: "Bilingüe · Asegurados · Eco-friendly" },

  // Language toggle
  "lang.en": { en: "EN", es: "EN" },
  "lang.es": { en: "ES", es: "ES" },
  "lang.switch": { en: "Switch language", es: "Cambiar idioma" },

  // Loader
  "loader.brand": { en: "General Cleaning Services, LLC", es: "General Cleaning Services, LLC" },
};

export function useT() {
  const { lang } = useLang();
  return {
    lang,
    t: (v) => t(v, lang),
    ui: (key) => t(UI[key], lang),
  };
}
